import actionTypes from './actionTypes';
import { addNotification } from './notification';
import { notificationTypes } from '../../constants';
import Model from '../../utils/model';

import ValueSource from '../../sources/value';

const renameProp = (oldProp, newProp, { [oldProp]: old, ...others }) => ({
    [newProp]: old,
    ...others,
});

export const setLoading = () => ({
    type: actionTypes.VALUE_EDITOR_LOADING,
    payload: null,
});

export const setLoaded = () => ({
    type: actionTypes.VALUE_EDITOR_LOADED,
    payload: null,
});

export const setSaving = () => ({
    type: actionTypes.VALUE_EDITOR_SAVING,
    payload: null,
});

export const setSaved = () => ({
    type: actionTypes.VALUE_EDITOR_SAVED,
    payload: null,
});

export const setDuplicateWarning = value => ({
    type: actionTypes.VALUE_EDITOR_SET_DUPLICATE_WARNING,
    payload: value,
});

export const setValue = (value, typeElements) => ({
    type: actionTypes.VALUE_EDITOR_SET_VALUE,
    payload: { value, typeElements },
});

export const setDeveloperName = developerName => ({
    type: actionTypes.VALUE_EDITOR_SET_VALUE_DEVELOPER_NAME,
    payload: developerName,
});

export const setContentType = contentType => ({
    type: actionTypes.VALUE_EDITOR_SET_VALUE_CONTENT_TYPE,
    payload: contentType,
});

export const setTypeElementId = typeElementId => ({
    type: actionTypes.VALUE_EDITOR_SET_VALUE_TYPE_ELEMENT_ID,
    payload: typeElementId,
});

export const setDefaultContentValue = defaultContentValue => ({
    type: actionTypes.VALUE_EDITOR_SET_VALUE_DEFAULT_CONTENT_VALUE,
    payload: defaultContentValue,
});

export const setObjectData = (objectData, valueEditorId) => ({
    type: actionTypes.VALUE_EDITOR_SET_VALUE_OBJECT_DATA,
    payload: { objectData, valueEditorId },
});

export const setObjectDataContentValue = (target, valueEditorId) => ({
    type: actionTypes.VALUE_EDITOR_SET_VALUE_OBJECT_DATA_CONTENT_VALUE,
    payload: { target, valueEditorId },
});

export const setContentFormat = contentFormat => ({
    type: actionTypes.VALUE_EDITOR_SET_VALUE_CONTENT_FORMAT,
    payload: contentFormat,
});

export const setIsFixed = isFixed => ({
    type: actionTypes.VALUE_EDITOR_SET_VALUE_IS_FIXED,
    payload: isFixed,
});

export const setAccess = access => ({
    type: actionTypes.VALUE_EDITOR_SET_VALUE_ACCESS,
    payload: access,
});

export const setIsVersionless = isVersionless => ({
    type: actionTypes.VALUE_EDITOR_SET_VALUE_IS_VERSIONLESS,
    payload: isVersionless,
});

export const setDeveloperSummary = developerSummary => ({
    type: actionTypes.VALUE_EDITOR_SET_VALUE_DEVELOPER_SUMMARY,
    payload: developerSummary,
});

export const deleteObjectData = (valueEditorId, internalId) => ({
    type: actionTypes.VALUE_EDITOR_DELETE_OBJECT_DATA,
    payload: { valueEditorId, internalId },
});

export const pushBreadcrumb = (valueEditorId, typeName, onClick) => ({
    type: actionTypes.VALUE_EDITOR_PUSH_BREADCRUMB,
    payload: { valueEditorId, typeName, onClick },
});

export const popBreadcrumb = () => ({
    type: actionTypes.VALUE_EDITOR_POP_BREADCRUMB,
});

export const setActiveBreadcrumb = breadcrumbActiveId => ({
    type: actionTypes.VALUE_EDITOR_SET_ACTIVE_BREADCRUMB,
    payload: { breadcrumbActiveId },
});

/**
 * Insert a new 'empty' objectData object for the specified type.
 *
 * We use the internalId to locate the appropriate place in the Value object structure.
 * If the internalId is empty, [], then this indicates a top-level objectData, otherwise
 * we find a matching value within the structure.  Typically the Value object looks
 * like this, abreviated,
 *
 * { contentType
 *   developerName
 *   contentValue
 *   valueEditorId    95876145-ed27-d119-b696-c7c8b99c718b
 *   objectData [
 *     {
 *       developerName
 *       contentValue
 *       properties [
 *         {
 *            developerName
 *            contentValue
 *            valueEditorId     5c557d42-6720-4497-af84-9665d0f837aa
 *            objectData [
 *               developerName
 *               contentValue
 *               properties [
 *                 {
 *                   developerName
 *                   contentValue
 *                   valueEditorId     aaaaaaaa-6720-4497-af84-9665d0f837bb
 *                   objectData: null
 *                 }
 *               ]
 *            ]
 *         }
 *       ]
 *     }
 *   ]
 * }
 *
 * So for example to add objectData at the bottom of the tree above we would:
 *
 *     newObjectData('aaaaaaaa-6720-4497-af84-9665d0f837bb', type);
 *
 * objectData lists are a single item for an Object or multiple items for a List
 *
 * @param {String} valueEditorId the id to reference the objectData item in the tree
 * @param {Object} type the Type definition, used as the proforma for a new Value
 */
export const newObjectData = (valueEditorId, type) => (dispatch) => {

    // For this new default object data, construct an 'empty' default.
    if (type && type.properties) {
        const objectData = {
            developerName: type.developerName,
            isSelected: false,
            order: 0,
            internalId: Model.guid(),
            externalId: null,
            properties: type.properties.map(p => (
                {
                    contentFormat: '',
                    contentType: p.contentType,
                    contentValue: null,
                    valueEditorId: Model.guid(),
                    developerName: p.developerName,
                    objectData: null,
                    typeElementId: p.typeElementId,
                    typeElementPropertyId: p.id,
                }
            )),
            typeElementBindingDeveloperName: null,
            typeElementId: type.id,
        };
        dispatch(setObjectData(objectData, valueEditorId));
    } else {
        dispatch(addNotification({
            type: notificationTypes.ERROR,
            message: 'Unknown or malformed Type definition when creating a new Value',
            isPersistent: true,
        }));
    }
};

export const saveValue = (value, authenticationToken, tenantId, force = false) => async (dispatch) => {

    const newValue = renameProp('objectData', 'defaultObjectData', value);
    dispatch(setSaving());

    const errorHandling = (error) => {
        dispatch(addNotification({
            type: notificationTypes.ERROR,
            message: error.message,
            isPersistent: true,
        }));
        dispatch(setSaved());
    };

    const trySaveValue = async () => {
        try {
            // Saving returns an id for new items and updates various auditing timestamps etc. which
            // may be needed by the caller.
            const updatedValue = await ValueSource.save(newValue, tenantId, authenticationToken);
            dispatch(setSaved());
            dispatch(setDuplicateWarning(false));
            return updatedValue;

        } catch (error) {
            errorHandling(error);
        }
        return null;
    };

    if (force || value.id !== null) {
        return trySaveValue();
    }

    return ValueSource.query(`?filter=developerName eq '${value.developerName}'`, authenticationToken, tenantId)
        .then((foundValues) => {
            if (foundValues && foundValues.length > 0) {
                // then show the duplicate value warning to the user
                dispatch(setSaved());
                dispatch(setDuplicateWarning(true));
                return false;
            }
            // if not, then save the value as normal
            return trySaveValue();
        }).catch(errorHandling);
};

export const fetchValue = (id, typeElements, authenticationToken, tenantId) => async (dispatch) => {

    dispatch(setLoading());
    try {
        const value = await ValueSource.get(id, authenticationToken, tenantId);
        const newValue = renameProp('defaultObjectData', 'objectData', value);

        // Populate structure with valueEditorId so we can locate each
        // chunk of hierarchical data to edit/view as we traverse down
        // the breadcrumb trail.
        const addEditorId = (val) => {
            val.valueEditorId = Model.guid();
            if (val.objectData) {
                val.objectData.map(od => od.properties.map(p => addEditorId(p)));
            }
            return val;
        };

        const newValue2 = addEditorId(newValue);

        dispatch(setValue(newValue2, typeElements));

        // For an existing value add the next trail in the breadcrumbs
        if (newValue2.objectData) {
            dispatch(pushBreadcrumb(
                newValue2.valueEditorId,
                newValue2.typeElementDeveloperName,
                () => dispatch(setActiveBreadcrumb(newValue2.valueEditorId)),
            ));
        }


    } catch (error) {
        dispatch(addNotification({
            type: notificationTypes.ERROR,
            message: error.message,
            isPersistent: true,
        }));
        dispatch(setLoaded());
    }
};

export const setPropertyFilter = (selectedProperties, typeElementId) => ({
    type: actionTypes.VALUE_EDITOR_SET_PROPERTY_FILTER,
    payload: { selectedProperties, typeElementId },
});
